import { motion } from "framer-motion";
import {
  FiTrendingUp,
  FiTarget,
  FiMap,
  FiUsers,
  FiCompass,
  FiGitBranch,
  FiLayers,
  FiCpu,
  FiArrowRight,
  FiAward,
  FiFileText,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import {
  FaRocket,
  FaTrophy,
  FaBuilding,
  FaLightbulb,
  FaBrain
} from "react-icons/fa";
import { IoSparkles } from "react-icons/io5";
import "./AiCoe.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
};

const solutions = [
  {
    num: "01",
    icon: FaRocket,
    title: "Start Your AI Journey",
    desc: "Begin your AI journey with confidence, knowing that our CoE will provide the necessary support and guidance.",
  },
  {
    num: "02",
    icon: FiTrendingUp,
    title: "Stay Ahead of FOMO",
    desc: "Avoid falling behind the AI curve by partnering with our CoE, which will keep you updated on the latest advancements and opportunities.",
  },
  {
    num: "03",
    icon: FiCompass,
    title: "Keep Pace with the Industry",
    desc: "Our CoE will ensure that your organization remains competitive by continuously adapting to the evolving AI landscape.",
  },
  {
    num: "04",
    icon: FaLightbulb,
    title: "Innovate with New Business Models",
    desc: "Harness the power of AI to drive innovation and develop new business models that capitalize on emerging opportunities.",
  },
  {
    num: "05",
    icon: FaTrophy,
    title: "Taste Success",
    desc: "With our CoE by your side, you'll not only taste success but also sustain it by embracing AI-driven strategies tailored to your business needs.",
  },
];

const modeSteps = [
  {
    num: "01",
    title: "Schedule a Discovery Call",
    desc: "Unlock the hidden potential of AI in context of your product portfolio.",
  },
  {
    num: "02",
    title: "Decide on Product & Use Cases",
    desc: "Discuss and decide on a particular product from your portfolio and potential use cases to build.",
  },
  {
    num: "03",
    title: "Build the Product Roadmap",
    desc: "Define the Product Roadmap and development schedule for the product identified.",
  },
  {
    num: "04",
    title: "Build the Team",
    desc: "Build a hybrid team with your product experts, our AI Experts and rest of the product team.",
  },
  {
    num: "05",
    title: "Deliver & Calibrate",
    desc: "Delivery of the platform and continue calibrating the AI model for better results.",
  },
];

const roles = [
  {
    icon: FiTarget,
    color: "green",
    title: "AI Advisory",
    desc: "Collaborated with the client's team to identify optimal AI use cases and assemble proficient teams, crafting a strategic roadmap for AI integration.",
  },
  {
    icon: FiMap,
    color: "blue",
    title: "Product Roadmap Enhancement",
    desc: "Collaborated with existing teams to refine the product roadmap, seamlessly incorporating AI tools and establishing a clear schedule for implementation.",
  },
  {
    icon: FiUsers,
    color: "teal",
    title: "AI CoE Establishment",
    desc: "Assisted in the establishment of an AI Center of Excellence, empowering the client to assemble a proficient team of data scientists and engineers.",
  },
  {
    icon: FiGitBranch,
    color: "purple",
    title: "Model Calibration",
    desc: "Partnered with the customer success team to fine-tune AI models, enhancing efficiency and elevating team productivity.",
  },
];

const platforms = [
  {
    color: "green",
    icon: IoSparkles,
    title: "AI-Powered Decision Support Tools",
    desc: "Engineered tools facilitating rapid fact-finding and decision-making processes, amplifying organizational agility.",
  },
  {
    color: "blue",
    icon: FiLayers,
    title: "Unified Data Platform",
    desc: "Developed an AI-powered engine seamlessly aggregating unstructured data from diverse sources onto a unified platform, enhancing accessibility and insights generation.",
  },
  {
    color: "purple",
    icon: FiCpu,
    title: "AI-Powered Template Library",
    desc: "Developed an AI-powered engine seamlessly aggregating unstructured data from diverse sources onto a unified platform, enhancing accessibility and insights generation.",
  },
];

const techProcesses = [
  {
    num: "01",
    title: "Data Centralization",
    desc: "Implemented a centralized approach for aggregating unstructured data, leveraging connectors and manual upload mechanisms for comprehensive coverage.",
  },
  {
    num: "02",
    title: "RAG Pipeline with Chroma & Pinecone Vector DB",
    desc: "Implemented a centralized approach for aggregating unstructured data, leveraging connectors and manual upload mechanisms for comprehensive coverage.",
  },
  {
    num: "03",
    title: "Prompt Engineering & Data Cleansing",
    desc: "Executed prompt engineering and meticulous data cleansing procedures tailored to specific verticals, ensuring data integrity and relevance.",
  },
];

/* Reusable corner circles */
const CornerCircles = () => (
  <>
    <span className="corner-circle top-right" />
    <span className="corner-circle bottom-left" />
  </>
);

const AiCoe = () => {
  return (
    <div className="aicoe-page">
      {/* Hero */}
      <section className="aicoe-hero">
        <div className="aicoe-hero-orb orb-1" />
        <div className="aicoe-hero-orb orb-2" />
        <div className="aicoe-hero-orb orb-3" />

        <motion.div initial="hidden" animate="visible" variants={stagger} style={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={fadeUp} className="aicoe-hero-badge">
            <FaBrain size={16} /> AI Centre of Excellence
          </motion.div>
          <motion.h1 variants={fadeUp}>
            AI Centre of <span>Excellence</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="aicoe-hero-sub">
            Digital Engineering through value driven, customer focused, and
            design thinking approach
          </motion.p>
          {/* <motion.div variants={fadeUp} className="aicoe-hero-stats">
            <div className="aicoe-hero-stat">
              <div className="aicoe-hero-stat-num">50+</div>
              <div className="aicoe-hero-stat-label">AI Projects</div>
            </div>
            <div className="aicoe-hero-stat">
              <div className="aicoe-hero-stat-num">98%</div>
              <div className="aicoe-hero-stat-label">Client Satisfaction</div>
            </div>
            <div className="aicoe-hero-stat">
              <div className="aicoe-hero-stat-num">3x</div>
              <div className="aicoe-hero-stat-label">Faster Delivery</div>
            </div>
          </motion.div> */}
        </motion.div>
      </section>

      {/* Intro */}
      <motion.div
        className="aicoe-intro"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={stagger}
      >
        <motion.div className="aicoe-intro-card" variants={fadeUp}>
          <CornerCircles />
          <h3>Uncertainty Around AI</h3>
          <p>
            In the rapidly evolving landscape of AI, uncertainty is inevitable,
            hindering organizations from maximizing its potential.
          </p>
        </motion.div>
        <motion.div className="aicoe-intro-card" variants={fadeUp}>
          <CornerCircles />
          <h3>Our AI CoE Solution</h3>
          <p>
            We propose establishing an AI Center of Excellence (CoE) in India,
            leveraging our outsourced model.
          </p>
          <ul>
            <li>
              Our CoE will serve as your strategic partner, providing
              assessment, guidance, and implementation based on our expertise in
              your business domain.
            </li>
            <li>
              We ensure continuous course correction to keep pace with the
              dynamic changes in AI, enabling you to stay ahead of the curve.
            </li>
          </ul>
        </motion.div>
      </motion.div>

      {/* Solutions */}
      <section className="aicoe-section">
        <motion.div
          className="aicoe-section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2>Our AI CoE <span>Solution</span></h2>
          <p>A step-by-step approach to AI transformation</p>
        </motion.div>

        <motion.div
          className="aicoe-solutions-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {solutions.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.num} className="aicoe-sol-card" variants={scaleIn}>
                <CornerCircles />
                <span className="aicoe-sol-number">{s.num}</span>
                <div className="aicoe-sol-icon">
                  <Icon />
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Mode of Operation */}
      <section className="aicoe-mode-section">
        <div className="aicoe-mode-inner">
          <motion.div
            className="aicoe-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <h2>Mode of <span>Operation</span></h2>
            <p>Our structured approach to delivering AI excellence</p>
          </motion.div>

          <motion.div
            className="aicoe-mode-steps"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            {modeSteps.map((step) => (
              <motion.div key={step.num} className="aicoe-mode-step" variants={scaleIn}>
                <CornerCircles />
                <div className="aicoe-mode-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* AI Case Study — Classified sections */}
      <section className="aicoe-case-section container">
        <motion.div
          className="aicoe-section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <div className="aicoe-case-badge">
            <FiFileText size={14} /> Case Study
          </div>
          <h2>AI Case Study: Driving AI Maturity in <span>Product Portfolio</span></h2>
        </motion.div>

        {/* Client Overview panel */}
        <motion.div
          className="aicoe-case-panel"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={scaleIn}
        >
          <CornerCircles />
          <div className="aicoe-case-panel-header">
            <div className="aicoe-case-panel-icon green">
              <FaBuilding />
            </div>
            <h3>Client Overview</h3>
          </div>
          <p>
            Our esteemed client, a US-based tech company, pioneers the fusion
            of technology and human judgment, leveraging AI to expedite
            decision-making processes.
          </p>
        </motion.div>

        {/* SpringUp's Role panel with sub-cards */}
        <motion.div
          className="aicoe-case-panel"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeUp}
        >
          <CornerCircles />
          <div className="aicoe-case-panel-header">
            <div className="aicoe-case-panel-icon blue">
              <FiAward />
            </div>
            <h3>SpringUp's Role</h3>
          </div>
          <p>
            SpringUp Labs played a pivotal role in augmenting the client's AI
            capabilities, spearheaded by our Principal AI Engineer, who
            provided invaluable contributions.
          </p>

          <motion.div
            className="aicoe-case-subgrid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            {roles.map((r) => {
              const Icon = r.icon;
              return (
                <motion.div key={r.title} className="aicoe-case-subcard" variants={scaleIn}>
                  <CornerCircles />
                  <div className={`aicoe-subcard-icon ${r.color}`}>
                    <Icon />
                  </div>
                  <h4>{r.title}</h4>
                  <p>{r.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Platforms Developed */}
        <motion.div
          className="aicoe-section-header"
          style={{ marginTop: 56 }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2>Platforms <span>Developed</span></h2>
          <p>Discover our AI achievements</p>
        </motion.div>

        <motion.div
          className="aicoe-platforms-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {platforms.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.title} className="aicoe-platform-card" variants={scaleIn}>
                <CornerCircles />
                <div className={`aicoe-platform-bar ${p.color}`} />
                <div className="aicoe-platform-body">
                  <div className={`aicoe-platform-icon ${p.color}`}>
                    <Icon />
                  </div>
                  <h4>{p.title}</h4>
                  <p>{p.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Tech & Process */}
      <section className="aicoe-section">
        <motion.div
          className="aicoe-section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2>Technology and Processes <span>Implemented</span></h2>
        </motion.div>

        <motion.div
          className="aicoe-tech-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {techProcesses.map((t) => (
            <motion.div key={t.num} className="aicoe-tech-card" variants={scaleIn}>
              <CornerCircles />
              <div className="aicoe-tech-num">{t.num}</div>
              <div>
                <h4>{t.title}</h4>
                <p>{t.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <motion.section
        className="aicoe-cta"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={stagger}
      >
        <motion.h2 variants={fadeUp}>Ready to Start Your AI Journey?</motion.h2>
        <motion.p variants={fadeUp}>
          By seamlessly integrating cutting-edge technology and meticulous
          processes, SpringUp Labs fosters AI maturity within your portfolio.
        </motion.p>
        <motion.button variants={fadeUp} className="aicoe-cta-btn">
          {/* Get Started <FiArrowRight size={18} /> */}
          <Link to="/contact" className=" text-decoration-none text-white">
            Get Started <FiArrowRight size={18} />
          </Link>
        </motion.button>
      </motion.section>
    </div>
  );
};

export default AiCoe;
